import { MenuItem, Skeleton } from "@chakra-ui/react";
import React from "react";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { sendPasswordReset } from "src/generated/hooks/form-service/requests";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { useOrgConfig } from "src/hooks/useOrgConfig";

type Props = {
  person: {
    person_id: string;
    active: boolean;
  };
};

export const ResetPasswordMenuItem: React.FC<Props> = ({ person }) => {
  const organization = useOrganization();
  const toast = useAvelaToast();
  const orgConfigRD = useOrgConfig<OrgConfig.LoginConfig>("Login");

  const dialog = useConfirmationDialog({
    header: "Send password reset link?",
    body: "This will send an email with a link to reset their password. Their current password will remain active until they set a new one.",
    confirmButton: {
      label: "Yes, send password reset",
    },
  });
  const resetPasswordHandler = React.useCallback(async () => {
    if (!organization.hasData()) {
      throw new Error("Invalid organization");
    }
    if (
      await dialog.confirm({
        onConfirm: (helpers) => {
          helpers.setButton({
            isLoading: true,
            loadingText: "Sending...",
            label: "",
          });
        },
      })
    ) {
      try {
        await sendPasswordReset({
          path: { personId: person.person_id },
        });
        toast({
          title: "Password reset link sent successfully",
          status: "success",
        });
      } catch (error) {
        console.error(error);
        toast.genericError();
      }
      dialog.helpers.close();
    }
  }, [organization, dialog, person.person_id, toast]);

  if (!person.active) {
    return null;
  }

  return (
    <>
      <RemoteDataView
        remoteData={orgConfigRD.map((data) => data?.admin[0]?.method === "sso")}
        loading={<Skeleton height="2rem" />}
        error={() => null}
      >
        {(isSSOOrg) => (
          <ConditionalTooltip
            showTooltip={isSSOOrg}
            label="Password reset is unavailable for organization with SSO."
          >
            <MenuItem onClick={resetPasswordHandler} isDisabled={isSSOOrg}>
              Reset password
            </MenuItem>
          </ConditionalTooltip>
        )}
      </RemoteDataView>
      {dialog.confirmationDialog}
    </>
  );
};
